import { useState, useRef, useCallback } from 'react'
import { Camera, Upload, X, RefreshCw } from 'lucide-react'
import { processSignatureImage, type ProcessedSignature } from '../utils/signatureProcessor'

interface SignatureUploadProps {
  onSignatureChange: (signature: ProcessedSignature | null) => void
}

export function SignatureUpload({ onSignatureChange }: SignatureUploadProps) {
  const [preview, setPreview] = useState<string | null>(null)
  const [processing, setProcessing] = useState(false)
  const [threshold, setThreshold] = useState(200)
  const fileInputRef = useRef<HTMLInputElement>(null)
  const cameraInputRef = useRef<HTMLInputElement>(null)
  const originalFileRef = useRef<File | null>(null)

  const processFile = useCallback(
    async (file: File, luminanceThreshold: number) => {
      setProcessing(true)
      try {
        const processed = await processSignatureImage(file, {
          maxWidth: 400,
          maxHeight: 150,
          luminanceThreshold,
        })
        setPreview(processed.dataUrl)
        onSignatureChange(processed)
      } catch (error) {
        console.error('Error processing signature:', error)
        alert('Erro ao processar a imagem. Por favor, tente novamente.')
      } finally {
        setProcessing(false)
      }
    },
    [onSignatureChange]
  )

  const handleFileChange = useCallback(
    async (e: React.ChangeEvent<HTMLInputElement>) => {
      const file = e.target.files?.[0]
      if (!file) return

      originalFileRef.current = file
      await processFile(file, threshold)
    },
    [processFile, threshold]
  )

  const handleThresholdChange = useCallback(
    async (newThreshold: number) => {
      setThreshold(newThreshold)
      if (originalFileRef.current) {
        await processFile(originalFileRef.current, newThreshold)
      }
    },
    [processFile]
  )

  const clearSignature = useCallback(() => {
    setPreview(null)
    originalFileRef.current = null
    onSignatureChange(null)
    if (fileInputRef.current) fileInputRef.current.value = ''
    if (cameraInputRef.current) cameraInputRef.current.value = ''
  }, [onSignatureChange])

  return (
    <div className="space-y-4">
      <input
        ref={fileInputRef}
        type="file"
        accept="image/*"
        onChange={handleFileChange}
        className="hidden"
      />
      <input
        ref={cameraInputRef}
        type="file"
        accept="image/*"
        capture="environment"
        onChange={handleFileChange}
        className="hidden"
      />

      {!preview ? (
        <div className="grid grid-cols-2 gap-3">
          <button
            type="button"
            onClick={() => cameraInputRef.current?.click()}
            className="btn-secondary flex-col gap-2 py-6"
          >
            <Camera className="w-8 h-8 text-primary-600" />
            <span className="text-sm">Tirar Foto</span>
          </button>
          <button
            type="button"
            onClick={() => fileInputRef.current?.click()}
            className="btn-secondary flex-col gap-2 py-6"
          >
            <Upload className="w-8 h-8 text-primary-600" />
            <span className="text-sm">Carregar Imagem</span>
          </button>
        </div>
      ) : (
        <div className="space-y-4">
          <div className="relative border-2 border-gray-200 rounded-xl p-4 bg-gray-50">
            {processing && (
              <div className="absolute inset-0 bg-white/80 flex items-center justify-center rounded-xl">
                <RefreshCw className="w-6 h-6 animate-spin text-primary-600" />
              </div>
            )}
            <img
              src={preview}
              alt="Assinatura processada"
              className="max-w-full h-auto mx-auto"
              style={{ maxHeight: '150px' }}
            />
          </div>

          <div className="space-y-2">
            <label className="label flex items-center justify-between">
              <span>Ajuste de contraste</span>
              <span className="text-xs text-gray-500">{threshold}</span>
            </label>
            <input
              type="range"
              min="100"
              max="250"
              value={threshold}
              onChange={(e) => handleThresholdChange(Number(e.target.value))}
              className="w-full h-2 bg-gray-200 rounded-lg appearance-none cursor-pointer accent-primary-600"
            />
            <p className="text-xs text-gray-500">
              Ajuste para remover o fundo do papel e manter apenas a assinatura
            </p>
          </div>

          <button
            type="button"
            onClick={clearSignature}
            className="btn-secondary w-full gap-2"
          >
            <X className="w-4 h-4" />
            Remover e escolher outra
          </button>
        </div>
      )}
    </div>
  )
}
